import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import type { UsersListFilters, UsersListResponse } from "./types";
import { USERS_LIST_QUERY_KEY } from "./constants";
import { buildUsersQuery } from "./build-users-query";

/**
 * Hand-written fetch — see CLAUDE.md → TD-07. The Orval-generated
 * client wraps responses in a `{ data, status, headers }` envelope at
 * the type level that our `httpClient` mutator does not return, so we
 * call `httpClient` directly until the mutator contract is fixed across
 * all features.
 */
async function fetchUsers(filters: UsersListFilters): Promise<UsersListResponse> {
  return httpClient<UsersListResponse>(`/api/admin/users${buildUsersQuery(filters)}`);
}

export function useUsersList(filters: UsersListFilters) {
  return useQuery({
    queryKey: [
      ...USERS_LIST_QUERY_KEY,
      filters.query.trim(),
      filters.role ?? "all",
      filters.status,
      filters.page,
      filters.limit,
    ] as const,
    queryFn: () => fetchUsers(filters),
    // Keep the previous page on screen while the next one loads — no
    // flash of skeleton when paging or typing into the search input.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}
